import { type TmCountryCode, TmFakeCountryCode } from '@/services/types'

export const enum FlagType {
  General,
  Extended,
  Other,
}

type FlagProps = {
  type: FlagType
  position: number
}

// prettier-ignore
const flagGeneralMap: (TmCountryCode | string)[] = [
  '',
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AN', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BM', 'BN', 'BO', 'BR', 'BS', 'BT', 'BV', 'BW', 'BY', 'BZ',
  'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN', 'CO', 'CR', 'CU', 'CV', 'CX', 'CY', 'CZ',
  'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ',
  'EC', 'EE', 'EG', 'EH', 'ER', 'ES', 'ET',
  'FI', 'FJ', 'FK', 'FM', 'FO', 'FR',
  'GA', 'GD', 'GE', 'GF', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY',
  'HK', 'HM', 'HN', 'HR', 'HT', 'HU',
  'IC', 'ID', 'IE', 'IL', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT',
  'JM', 'JO', 'JP',
  'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ',
  'LA', 'LB', 'LC', 'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY',
  'MA', 'MC', 'MD', 'ME', 'MG', 'MH', 'MK', 'ML',
  'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ',
  'NA', 'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ',
  'OM',
  'PA', 'PE', 'PF', 'PG', 'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PT', 'PW', 'PY',
  'QA',
  'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR',
  'SS', 'ST', 'SV', 'SY', 'SZ',
  'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO', 'TP', 'TR', 'TT', 'TV', 'TW', 'TY', 'TZ',
  'UA', 'UG', 'GB', 'UM', 'US', 'UY', 'UZ',// GB is here!
  'VA', 'VC', 'VE', 'VG', 'VI', 'VN', 'VU',
  'WF', 'WS',
  'YE',
  'ZA', 'ZM', 'ZR', 'ZW',
]

const flagExtendedMap: Record<string, number> = {
  BL: 0,
  JE: 1,
  XK: 2,
  SX: 3,
  MF: 3,
  GG: 4,
  IM: 5,
  YT: 6,
  PS: 7,
  LV: 8,
  CW: 9,
}

const countryCodeFlagAliases: Record<string, TmCountryCode | string> = {
  BQ: 'NL',
}

const flagExtendedMapLength = Math.max(...Object.values(flagExtendedMap)) + 1

const getPosition = (index: number, length: number) => index / (length - 1)

const getFlagGeneralPosition = (countryCode: string) =>
  getPosition(Math.max(0, flagGeneralMap.indexOf(countryCode)), flagGeneralMap.length)

const getFlagExtendedPosition = (countryCode: string) =>
  getPosition(flagExtendedMap[countryCode], flagExtendedMapLength)

export const getFlagProps = (rawCountryCode: TmCountryCode | string): FlagProps => {
  if (!rawCountryCode || rawCountryCode === TmFakeCountryCode) {
    return {
      type: FlagType.Other,
      position: 0,
    }
  }

  const countryCode = countryCodeFlagAliases[rawCountryCode] ?? rawCountryCode

  if (flagExtendedMap[countryCode]) {
    return {
      type: FlagType.Extended,
      position: getFlagExtendedPosition(countryCode),
    }
  }

  return {
    type: FlagType.General,
    position: getFlagGeneralPosition(countryCode),
  }
}
